import React, { useState, useEffect, useMemo } from "react";  
import {  
  Typography,  
  Card,  
  Button,  
  message,  
  Tabs,  
  Table,  
  Skeleton,  
  Select
} from "antd";  
import dayjs from "dayjs";  
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";  
import weekday from "dayjs/plugin/weekday";  
import customParseFormat from "dayjs/plugin/customParseFormat";  
import localeData from "dayjs/plugin/localeData";  
import utc from "dayjs/plugin/utc";  
import timezone from "dayjs/plugin/timezone";  
import {  
  exportAllSchedulesToExcel,  
  exportAllApplicationsToExcel,  
} from "../../utils/schedulingUtils/exportToXlsxUtils.js";  
import {  
  getAllApplications,  
  updateApplicationsWithScheduledTimes,  
} from "../../api/application.js";  
import { getVenues } from "../../api/venue.js";  
import { useSettings } from "../../contexts/settingsContext.js";  
import DateTimeSelector from "../../components/Scheduling/dateTimeSelector";  
import { scheduleApplicationsForAllVenues, estimateEndTimeForRequiredMinutes } from "../../utils/schedulingUtils/venueSchedulingUtils.js";  
import { loadState } from "../../stores/store.js";  
import { io as socketIOClient } from 'socket.io-client';
import { fetchProvinces } from "../../api/competition.js";
dayjs.extend(isSameOrBefore);  
dayjs.extend(weekday);  
dayjs.extend(customParseFormat);  
dayjs.extend(localeData);  
dayjs.extend(utc);  
dayjs.extend(timezone);  
  
const { Title } = Typography;  

const isRoundTwoApplication = (application) => Number(application?.round) === 2;

const getRoundTwoGroupEventStyleKey = (application) => {
  const groupId =
    application?.group?._id ||
    application?.group?.id ||
    application?.group?.group_id ||
    application?.group?.group_name;
  const eventId = application?.event?._id || application?.event?.id;
  const eventName = application?.event?.name || "";
  const eventStyle = application?.event?.style || "";

  return `r2-group-${groupId || "unknown"}-${eventId || eventName}-${eventStyle}`;
};

const dedupeRoundTwoGroupApplications = (applications = []) => {
  const uniqueApplications = new Map();

  applications.forEach((application) => {
    if (!application) return;

    const isRoundTwoGroupApplication =
      isRoundTwoApplication(application) && Boolean(application.group);

    if (!isRoundTwoGroupApplication) {
      uniqueApplications.set(application._id || JSON.stringify(application), application);
      return;
    }

    const dedupeKey = getRoundTwoGroupEventStyleKey(application);
    const existing = uniqueApplications.get(dedupeKey);

    if (!existing) {
      uniqueApplications.set(dedupeKey, application);
      return;
    }

    const existingHasTime = Boolean(existing?.scheduledDate && existing?.startTime && existing?.endTime);
    const currentHasTime = Boolean(application?.scheduledDate && application?.startTime && application?.endTime);

    if (!existingHasTime && currentHasTime) {
      uniqueApplications.set(dedupeKey, application);
    }
  });

  return Array.from(uniqueApplications.values());
};

const formatMinutesToHuman = (minutes) => {
  const hrs = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hrs > 0 && mins === 0) return `${hrs} hour${hrs > 1 ? "s" : ""}`;
  if (hrs > 0) return `${hrs} hour${hrs > 1 ? "s" : ""} ${mins} minute${mins > 1 ? "s" : ""}`;
  return `${mins} minute${mins > 1 ? "s" : ""}`;
};
  
const SchedulingPage = () => {  
  const [times, setTimes] = useState({});  
  const [venues, setVenues] = useState([]);  
  const gapDuration = 0;
  const [selectedVenueTab, setSelectedVenueTab] = useState(null);  
  const [dateList, setDateList] = useState([]);  
  const { state: settings } = useSettings();  
  
  // State variables  
  const [allApplicationsPerVenue, setAllApplicationsPerVenue] = useState({});  
  const [unscheduledApplicationsPerVenue, setUnscheduledApplicationsPerVenue] = useState({});  
  const [loadingAllApplications, setLoadingAllApplications] = useState(false);  
  const [isScheduling, setIsScheduling] = useState(false);  
  const [provinces, setProvinces] = useState([]);
  const [provinceFilter, setProvinceFilter] = useState(null);
  const state = loadState();  

  const venueDurations = useMemo(() => {
    const map = {};
    Object.keys(allApplicationsPerVenue).forEach((venueId) => {
      const count = (allApplicationsPerVenue[venueId] || []).length;
      map[venueId] = count * 2; // each application = 2 minutes
    });
    return map;
  }, [allApplicationsPerVenue]);

  const [showCalcDetails, setShowCalcDetails] = useState(false);

  const calculationDetails = useMemo(() => {
    if (!dateList || dateList.length === 0) return {};
    const perAppMinutes = 2;
    const numDays = dateList.length;
    const details = {};

    Object.keys(allApplicationsPerVenue || {}).forEach((venueId) => {
      const apps = allApplicationsPerVenue[venueId] || [];
      const totalApps = apps.length;
      const totalMinutes = totalApps * perAppMinutes;
      const basePerDay = numDays > 0 ? Math.ceil(totalMinutes / numDays) : 0;

      const grouped = {};
      apps.forEach((a) => {
        const key = `${a.event?.name || 'unknown'}::${a.event?.style || 'unknown'}`;
        grouped[key] = (grouped[key] || 0) + 1;
      });

      let smallSpillApps = 0;
      Object.values(grouped).forEach((count) => {
        const remainder = numDays > 0 ? count % numDays : 0;
        if (remainder > 0 && remainder <= 2) {
          smallSpillApps += remainder;
        }
      });

      const smallSpillMinutes = smallSpillApps * perAppMinutes;
      const requiredPerDay = basePerDay + smallSpillMinutes;

      const venueObj = venues.find((v) => v._id === venueId) || null;
      const defaultStart = dayjs().hour(8).minute(30).second(0);
      let estEnd = null;
      try {
        if (venueObj && requiredPerDay > 0) {
          estEnd = estimateEndTimeForRequiredMinutes(defaultStart, requiredPerDay, venueObj);
        }
      } catch (e) {
        estEnd = defaultStart.clone().add(requiredPerDay, 'minute');
      }

      details[venueId] = {
        venueName: venueObj?.venue || (venueId === 'noVenue' ? 'No Venue' : 'Unknown Venue'),
        totalApps,
        totalMinutes,
        basePerDay,
        smallSpillApps,
        smallSpillMinutes,
        requiredPerDay,
        estEnd,
      };
    });

    return details;
  }, [allApplicationsPerVenue, dateList, venues]);
  
  // Map group size to group type  
  const mapSizeToType = (size) => {  
    if (size === 2) return "duet";  
    if (size === 3) return "trio";  
    if (size >= 4 && size <= 9) return "small group";  
    if (size >= 10 && size <= 19) return "large group";  
    if (size >= 20) return "production line";  
    return "solo";  
  };  
  
  // Function to load scheduled applications  
  const loadScheduledApplications = async () => {  
    setLoadingAllApplications(true);  
    try {  
      // Fetch round 2 applications only
      const fetchedProvinces = await fetchProvinces(settings.selectedCompetition._id);
      setProvinces(fetchedProvinces);
      const data = (await getAllApplications(2)) || [];

  // Only include round 2 applications that are paid, but also include
  // applications that are already fully graded so they appear as green even if unpaid
  let filteredData = data.filter(
    (app) => app && isRoundTwoApplication(app) && (app.paid === true || app.fully_graded === true)
  );

      filteredData = dedupeRoundTwoGroupApplications(filteredData);

      if (provinceFilter && provinceFilter !== "all") {
        filteredData = filteredData.filter((c) => c.province === provinceFilter);
      }
  
      console.log(filteredData);
      // Organize applications per venue  
      const applicationsPerVenue = {};  
      filteredData.forEach((app) => {  
        // Check if event and venue are properly populated  
        const venueId = app.event && app.event.venue && app.event.venue._id;  
        if (venueId) {  
          if (!applicationsPerVenue[venueId]) {  
            applicationsPerVenue[venueId] = [];  
          }  
          applicationsPerVenue[venueId].push(app);  
        } else {  
          // Handle applications without a venue  
          if (!applicationsPerVenue["noVenue"]) {  
            applicationsPerVenue["noVenue"] = [];  
          }  
          applicationsPerVenue["noVenue"].push(app);  
        }  
      });  
  
      // Set applications to state  
      setAllApplicationsPerVenue(applicationsPerVenue);  
      setLoadingAllApplications(false);  
  
      // Set the selected tab to the first venue  
      const venueIds = Object.keys(applicationsPerVenue);  
      if (venueIds.length > 0) {  
        setSelectedVenueTab(venueIds[0]);  
      }  
    } catch (error) {  
      console.error(`Failed to load applications: ${error}`);  
      message.error("Failed to load applications.");  
      setLoadingAllApplications(false);  
    }  
  };  
  
  // Load scheduled applications when component mounts  
  useEffect(() => {  
    loadScheduledApplications();  
    // eslint-disable-next-line  
  }, [settings.selectedRound, provinceFilter]);  
  
  // Fetch venues when component mounts  
  useEffect(() => {  
    const fetchVenues = async () => {  
      try {  
        const data = await getVenues();  
        setVenues(data);  
      } catch (error) {  
        console.error("Error fetching venues:", error);  
        message.error("Failed to load venues.");  
      }  
    };  
    fetchVenues();  
  }, []);  
  
  const setDateRange = (startDate, endDate) => {
    if (!startDate || !endDate) {
      setDateList([]);
      setTimes({});
      return;
    }

    const start = startDate.startOf("day");
    const end = endDate.startOf("day");
    const nextDates = [];
    let current = start.clone();

    while (current.isSameOrBefore(end, "day")) {
      nextDates.push(current);
      current = current.add(1, "day");
    }

    const validDateKeys = new Set(
      nextDates.map((date) => date.format("YYYY-MM-DD"))
    );

    setDateList(nextDates);
    setTimes((prevTimes) => {
      const nextTimes = {};
      Object.keys(prevTimes).forEach((dateKey) => {
        if (validDateKeys.has(dateKey)) {
          nextTimes[dateKey] = prevTimes[dateKey];
        }
      });

      // Determine sensible default end times based on event durations
      const perAppMinutes = 2; // each application assumed 2 minutes
      const numDays = nextDates.length;

      // Compute total minutes per venue from currently loaded applications
      const venueTotals = {};
      Object.keys(allApplicationsPerVenue || {}).forEach((venueId) => {
        const count = (allApplicationsPerVenue[venueId] || []).length;
        venueTotals[venueId] = count * perAppMinutes;
      });

      // For each venue, compute required minutes per day (spread across selected days)
      // and account for small spillovers per event style so near-complete styles
      // can be kept on the same day.
      const perVenueRequiredPerDay = {};

      if (numDays > 0) {
        Object.keys(allApplicationsPerVenue || {}).forEach((venueId) => {
          const apps = allApplicationsPerVenue[venueId] || [];

          // Total minutes for venue
          const totalVenueMinutes = apps.length * perAppMinutes;
          let basePerDay = Math.ceil(totalVenueMinutes / numDays);

          // Compute small-spill cushion from grouping by event+style
          const grouped = {};
          apps.forEach((a) => {
            const key = `${a.event?.name || 'unknown'}::${a.event?.style || 'unknown'}`;
            grouped[key] = (grouped[key] || 0) + 1;
          });

          let smallSpillMinutes = 0;
          Object.values(grouped).forEach((count) => {
            const remainderApps = count % numDays;
            // If only 1 or 2 apps spill to the next day, add them to the day's required minutes
            if (remainderApps > 0 && remainderApps <= 2) {
              smallSpillMinutes += remainderApps * perAppMinutes;
            }
          });

          perVenueRequiredPerDay[venueId] = basePerDay + smallSpillMinutes;
        });
      }

      nextDates.forEach((date) => {
        const dateKey = date.format("YYYY-MM-DD");
        if (!nextTimes[dateKey]) {
          const defaultStart = dayjs().hour(8).minute(30).second(0);
          const entry = { startTime: defaultStart };

          if (Object.keys(perVenueRequiredPerDay).length > 0 && venues && venues.length > 0) {
            // For each venue compute an estimated endTime that accounts for breaks,
            // then choose the latest endTime so the day fits all venues.
            const venueEndTimes = Object.keys(perVenueRequiredPerDay).map((venueId) => {
              const venueObj = venues.find((v) => v._id === venueId) || null;
              const requiredMinutes = perVenueRequiredPerDay[venueId] || 0;
              if (!venueObj || requiredMinutes <= 0) return null;
              try {
                return estimateEndTimeForRequiredMinutes(defaultStart, requiredMinutes, venueObj);
              } catch (e) {
                return defaultStart.clone().add(requiredMinutes, 'minute');
              }
            }).filter(Boolean);

            if (venueEndTimes.length > 0) {
              // pick latest
              const latest = venueEndTimes.reduce((cur, nxt) => (nxt.isAfter(cur) ? nxt : cur), venueEndTimes[0]);
              entry.endTime = latest;
            }
          }

          nextTimes[dateKey] = entry;
        }
      });

      return nextTimes;
    });
  };
  
  const onTimeChange = (dateStr, actionType, data) => {  
    setTimes((prevTimes) => {  
      const dateTimes = prevTimes[dateStr] || {};  
      switch (actionType) {  
        case 'startTime':  
        case 'endTime':  
          return {  
            ...prevTimes,  
            [dateStr]: {  
              ...dateTimes,  
              [actionType]: data,  
            },  
          };  
        case 'addBreak':  
          const newBreaks = dateTimes.breaks ? [...dateTimes.breaks] : [];  
          newBreaks.push({  
            breakStartTime: null,  
            breakEndTime: null,  
          });  
          return {  
            ...prevTimes,  
            [dateStr]: {  
              ...dateTimes,  
              breaks: newBreaks,  
            },  
          };  
        case 'breakTimeChange':  
          {  
            const { index, timeType, time } = data;  
            const updatedBreaks = dateTimes.breaks ? [...dateTimes.breaks] : [];  
  
            // Get the day's startTime and endTime  
            const dayStartTime = dateTimes.startTime;  
            const dayEndTime = dateTimes.endTime;  
  
            // Validate that the break time is within the day's start and end times  
            if (time && dayStartTime && dayEndTime) {  
              if (time.isBefore(dayStartTime) || time.isAfter(dayEndTime)) {  
                message.error("Break times must be within the day's start and end times.");  
                return prevTimes; // Do not update the state if invalid  
              }  
  
              // Also ensure that breakStartTime is before breakEndTime  
              const otherTimeType = timeType === 'breakStartTime' ? 'breakEndTime' : 'breakStartTime';  
              const otherTime = updatedBreaks[index][otherTimeType];  
  
              if (otherTime) {  
                if (timeType === 'breakStartTime' && !time.isBefore(otherTime)) {  
                  message.error('Break start time must be before break end time.');  
                  return prevTimes;  
                }  
                if (timeType === 'breakEndTime' && !time.isAfter(otherTime)) {  
                  message.error('Break end time must be after break start time.');  
                  return prevTimes;  
                }  
              }  
            }  
  
            updatedBreaks[index] = {  
              ...updatedBreaks[index],  
              [timeType]: time,  
            };  
  
            return {  
              ...prevTimes,  
              [dateStr]: {  
                ...dateTimes,  
                breaks: updatedBreaks,  
              },  
            };  
          }  
        case 'removeBreak':  
          const breaksAfterRemoval = dateTimes.breaks  
            ? dateTimes.breaks.filter((_, i) => i !== data.index)  
            : [];  
          return {  
            ...prevTimes,  
            [dateStr]: {  
              ...dateTimes,  
              breaks: breaksAfterRemoval,  
            },  
          };  
        default:  
          return prevTimes;  
      }  
    });  
  };  
  
  const areDatesAndTimesSelected = () => {  
    if (dateList.length === 0) {  
      return false;  
    }  
  
    // For each date, check if both startTime and endTime are selected  
    for (let date of dateList) {  
      const dateStr = date.format("YYYY-MM-DD");  
      const timesForDate = times[dateStr];  
      if (!timesForDate || !timesForDate.startTime || !timesForDate.endTime) {  
        return false;  
      }  
    }  
  
    return true;  
  };  
  
  // Function to update scheduled applications in the database  
  const updateScheduledApplications = async (applications) => {  
    try {  
      // Prepare data to send to the backend  
      const applicationUpdates = applications.map((app) => ({  
        _id: app._id,  
        date: app.startTime.toISOString(), // Use ISO string format  
      }));  
  
      // Send updates in batches to avoid payload size limits
      const batchSize = 1000;
      for (let i = 0; i < applicationUpdates.length; i += batchSize) {
        const batch = applicationUpdates.slice(i, i + batchSize);
        await updateApplicationsWithScheduledTimes(batch);
      }
    } catch (error) {  
      console.error("Error updating scheduled applications:", error);  
      message.error("Failed to update scheduled applications.");  
    }  
  };  
  
  const scheduleAllVenues = async () => {  
    if (dateList.length === 0) {  
      message.error(  
        "Please select dates with start and end times for each day."  
      );  
      return;  
    }  
  
    if (!areDatesAndTimesSelected()) {  
      message.error(  
        "Please select start and end times for all selected dates."  
      );  
      return;  
    }  
  
    setIsScheduling(true); // Start the loader  
    try {  
      // Fetch round 2 applications for all venues
      const data = (await getAllApplications(2)) || [];

  // Only schedule round 2 applications that have been paid, but include already fully graded ones
  let filteredData = data.filter(
    (app) => app && isRoundTwoApplication(app) && (app.paid === true || app.fully_graded === true)
  );

      filteredData = dedupeRoundTwoGroupApplications(filteredData);

      if (provinceFilter && provinceFilter !== "all") {
        filteredData = filteredData.filter((c) => c.province === provinceFilter);
      }
      // Organize applications per venue  
      const applicationsPerVenue = {};  
      filteredData.forEach((app) => {  
        // Check if event and venue are properly populated  
        if (!app.event || !app.event.venue || !app.event.venue._id) {  
          console.warn(  
            "Skipping application due to missing event or venue:",  
            app  
          );  
          return; // Skip this application  
        }  
        const venueId = app.event.venue._id; // Access venueId safely  
        if (!applicationsPerVenue[venueId]) {  
          applicationsPerVenue[venueId] = [];  
        }  
        applicationsPerVenue[venueId].push(app);  
      });  
  
      // Call the scheduling function  
      const { scheduledPerVenue, unscheduledPerVenue } =  
        scheduleApplicationsForAllVenues(  
          applicationsPerVenue,  
          dateList,  
          times,  
          venues,  
          gapDuration  
        );  

        console.log(scheduledPerVenue);
        console.log(unscheduledPerVenue);
  
      // Collect all scheduled applications  
      const allScheduledApplications = [];  
      Object.values(scheduledPerVenue).forEach((apps) => {  
        allScheduledApplications.push(...apps);  
      });  
  
      // Update scheduled applications in the database  
      await updateScheduledApplications(allScheduledApplications);  
  
      // Set unscheduled applications state  
      setUnscheduledApplicationsPerVenue(unscheduledPerVenue);  
  
      // If there are unscheduled applications, display a warning  
      const unscheduledCount = Object.values(unscheduledPerVenue).reduce(  
        (acc, apps) => acc + apps.length,  
        0  
      );  
      if (unscheduledCount > 0) {  
        message.warning(  
          `There are ${unscheduledCount} applications that could not be scheduled due to insufficient time slots.`  
        );  
      } else {  
        message.success("All applications have been scheduled successfully.");  
      }  
  
      // Reload the scheduled applications to reflect updates  
      await loadScheduledApplications();  
  
      // Set the selected tab to the first venue  
      const venueIds = Object.keys(scheduledPerVenue);  
      if (venueIds.length > 0) {  
        setSelectedVenueTab(venueIds[0]);  
      }  
    } catch (error) {  
      console.error("Error fetching applications:", error);  
      message.error("Failed to schedule applications.");  
    } finally {  
      setIsScheduling(false); // Stop the loader  
    }  
  };  
  
  // Function to handle export of all schedules  
  const handleExportAllSchedulesToExcel = () => {  
    exportAllSchedulesToExcel(allApplicationsPerVenue, venues);  
  };  
  
  // Define columns for the applications table  
  const columns = [  
    {
      title: 'Status',
      dataIndex: '',
      key: 'status',
      render: (application) => {
        if (application.fully_graded) {
          return <span style={{ color: 'green', fontWeight: 600 }}>Fully graded</span>;
        }
        return null;
      },
    },
    {  
      title: "Participants",  
      dataIndex: "",  
      key: "participants",  
      render: (application) => {  
        if (application.group && application.group.group_name) {  
          // Show group name and group members  
          const memberNames = (application.group.group || []).map(m => m.name).join(', ');  
          return `${application.group.group_name}`;  
        } else if (application.contestant) {  
          // Single contestant is a Contestant object  
          return `${application.contestant.first_name} ${application.contestant.last_name}`;  
        } else {  
          return "No Contestants";  
        }  
      },  
      sorter: (a, b) => {  
        const nameA =  
          (a.group && a.group.group_name) ||  
          (a.contestant && a.contestant.last_name) || "";  
        const nameB =  
          (b.group && b.group.group_name) ||  
          (b.contestant && b.contestant.last_name) || "";  
        return nameA.localeCompare(nameB);  
      },  
    },  
    {  
      title: "Group Type",  
      dataIndex: "",  
      key: "groupType",  
      render: (application) => {  
        if (application.group && application.group.group_size) {  
          return mapSizeToType(application.group.group_size);  
        } else {  
          return "solo";  
        }  
      },  
    },  
    {  
      title: "Event",  
      dataIndex: "event",  
      key: "event",  
      render: (event) =>  
        event ? `${event.name} - ${event.style}` : "No Event",  
    },  
    {  
      title: "Scheduled Start",  
      dataIndex: "date",  
      key: "date",  
      render: (date) =>  
        date  
          ? dayjs.utc(date).tz("Europe/Bucharest").format("YYYY-MM-DD HH:mm")  
          : "Not Scheduled",  
      sorter: (a, b) => new Date(a.date) - new Date(b.date),  
    },  
    {  
      title: "Venue",  
      dataIndex: "event",  
      key: "venue",  
      render: (event) => (event && event.venue ? event.venue.venue : "No Venue"),  
    },  
  ];  

  // Socket listener to update applications in real-time when grading completes
  useEffect(() => {
    const socket = socketIOClient();
    if (state.user && state.user._id) {
      socket.on('connect', () => {
        socket.emit('register', state.user._id);
      });

      socket.on('applicationFullyGraded', (payload) => {
        try {
          if (!payload || !payload.application) return;
          const updatedApp = payload.application;
          // Update the applications map to set fully_graded on the matching application
          setAllApplicationsPerVenue((prev) => {
            const newMap = { ...prev };
            for (const venueId of Object.keys(newMap)) {
              newMap[venueId] = newMap[venueId].map((app) =>
                app._id === updatedApp._id ? { ...app, fully_graded: updatedApp.fully_graded } : app
              );
            }
            return newMap;
          });
          message.success('An application was fully graded');
        } catch (e) {
          console.error('Error handling applicationFullyGraded in scheduling page', e);
        }
      });
    }

    return () => {
      try { socket.disconnect(); } catch (e) {}
    };
  }, [state.user]);
  
  return (  
    <div>  
      <div  
        style={{  
          marginBottom: "15px",  
          display: "flex",  
          alignItems: "center",  
          justifyContent: "space-between",  
        }}  
      >  
        <Title level={4} style={{ margin: 0 }}>  
          Scheduling  
        </Title>  
      </div>  
      <Card bordered={false}>  
        <DateTimeSelector  
          dateList={dateList}  
          setDateRange={setDateRange}
          times={times}  
          onTimeChange={onTimeChange}  
        />  
        {/* Calculation details panel moved here (under the date range selector) */}
        {!loadingAllApplications && Object.keys(allApplicationsPerVenue).length > 0 && (
          <div style={{ marginTop: 12, marginBottom: 12 }}>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: 'center' }}>
              {Object.keys(allApplicationsPerVenue).map((venueId) => {
                const venueName =
                  venueId === "noVenue"
                    ? "No Venue"
                    : venues.find((venue) => venue._id === venueId)?.venue || "Unknown Venue";
                const minutes = venueDurations[venueId] || 0;
                return (
                  <div
                    key={venueId}
                    style={{ padding: "6px 10px", border: "1px solid #f0f0f0", borderRadius: 4 }}
                  >
                    <strong>{venueName}</strong> - {formatMinutesToHuman(minutes)}
                  </div>
                );
              })}

              <Button type="link" onClick={() => setShowCalcDetails((s) => !s)}>
                {showCalcDetails ? 'Hide calculation details' : 'Show calculation details'}
              </Button>
            </div>

            {showCalcDetails && (
              <div style={{ marginTop: 12, padding: 12, background: '#fafafa', borderRadius: 6 }}>
                {Object.keys(calculationDetails).length === 0 ? (
                  <div>No dates selected or no applications loaded.</div>
                ) : (
                  <Table
                    size="small"
                    pagination={false}
                    dataSource={Object.keys(calculationDetails).map((venueId) => {
                      const d = calculationDetails[venueId];
                      return {
                        key: venueId,
                        venueName: d.venueName,
                        apps: d.totalApps,
                        totalTime: formatMinutesToHuman(d.totalMinutes),
                        basePerDay: formatMinutesToHuman(d.basePerDay),
                        spillApps: d.smallSpillApps,
                        spillMins: formatMinutesToHuman(d.smallSpillMinutes),
                        requiredPerDay: formatMinutesToHuman(d.requiredPerDay),
                        estEnd: d.estEnd ? d.estEnd.format('HH:mm') : '-',
                      };
                    })}
                    columns={[
                      { title: 'Venue', dataIndex: 'venueName', key: 'venueName' },
                      { title: 'Apps', dataIndex: 'apps', key: 'apps', align: 'right' },
                      { title: 'Total time', dataIndex: 'totalTime', key: 'totalTime', align: 'right' },
                      { title: 'Base/day', dataIndex: 'basePerDay', key: 'basePerDay', align: 'right' },
                      { title: 'Spill apps', dataIndex: 'spillApps', key: 'spillApps', align: 'right' },
                      { title: 'Spill mins', dataIndex: 'spillMins', key: 'spillMins', align: 'right' },
                      { title: 'Required/day', dataIndex: 'requiredPerDay', key: 'requiredPerDay', align: 'right' },
                      { title: 'Est end', dataIndex: 'estEnd', key: 'estEnd', align: 'right' },
                    ]}
                  />
                )}
              </div>
            )}
          </div>
        )}
        <div style={{ marginTop: "20px" }}>  
          {areDatesAndTimesSelected() && (  
            <Button  
              onClick={scheduleAllVenues}  
              type="primary"  
              loading={isScheduling}  
            >  
              Schedule All Venues  
            </Button>  
          )}  
        </div>  
      </Card>  
  
      {/* Display scheduled applications by default */}  
      <Card bordered={false} style={{ marginTop: "20px" }}>  
        {loadingAllApplications ? (  
          <Skeleton active />  
        ) : (  
          <div>  
            <div  
              style={{  
                display: "flex",  
                justifyContent: "space-between",  
                alignItems: "center",  
              }}  
            >  
              <Title level={4}>Scheduled Applications</Title>  
              <Select  
                value={provinceFilter}  
                onChange={value => setProvinceFilter(value)}  
                style={{ width: 250 }}  
                showSearch  
                allowClear  
                placeholder="Select a province"  
                >  
                <Select.Option value="">All</Select.Option>  
                {provinces && Array.isArray(provinces) &&  
                    provinces.map(prov => (  
                    <Select.Option key={prov._id} value={prov._id}>  
                        {prov.province} ({prov.location}) - {prov.date}  
                    </Select.Option>  
                    ))  
                }  
              </Select>
              <Button  
                onClick={() =>  
                  exportAllApplicationsToExcel(  
                    allApplicationsPerVenue,  
                    venues  
                  )  
                }  
                type="primary"  
              >  
                Export to Excel  
              </Button>  
            </div>  
            {!loadingAllApplications && Object.keys(allApplicationsPerVenue).length > 0 && (
              <div style={{ marginTop: 12, marginBottom: 12 }}>
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: 'center' }}>
                  {Object.keys(allApplicationsPerVenue).map((venueId) => {
                    const venueName =
                      venueId === "noVenue"
                        ? "No Venue"
                        : venues.find((venue) => venue._id === venueId)?.venue || "Unknown Venue";
                    const minutes = venueDurations[venueId] || 0;
                    return (
                      <div
                        key={venueId}
                        style={{ padding: "6px 10px", border: "1px solid #f0f0f0", borderRadius: 4 }}
                      >
                        <strong>{venueName}</strong> - {formatMinutesToHuman(minutes)}
                      </div>
                    );
                  })}

                </div>
              </div>
            )}
            <Tabs  
              activeKey={selectedVenueTab}  
              onChange={(key) => setSelectedVenueTab(key)}  
              type="card"  
            >  
              {Object.keys(allApplicationsPerVenue).map((venueId) => {  
                const applications = allApplicationsPerVenue[venueId];  
                const venueName =  
                  venueId === "noVenue"  
                    ? "No Venue"  
                    : venues.find(  
                        (venue) => venue._id === venueId  
                      )?.venue || "Unknown Venue";  
  
                // Sort applications by date  
                const sortedApplications = applications  
                  .slice()  
                  .sort((a, b) => {  
                    const dateA = a.date  
                      ? new Date(a.date)  
                      : new Date(0);  
                    const dateB = b.date  
                      ? new Date(b.date)  
                      : new Date(0);  
                    return dateA - dateB;  
                  });  
  
                // Prepare dataSource for the Table  
                const dataSource = sortedApplications.map(  
                  (application) => ({  
                    ...application,  
                    key: application._id,  
                  })  
                );  
  
                return (  
                  <Tabs.TabPane tab={venueName} key={venueId}>  
                    <Table  
                      dataSource={dataSource}  
                      columns={columns}  
                      rowKey="key"  
                      pagination={{ pageSize: 10 }}  
                      scroll={{ x: true }}  
                    />  
                  </Tabs.TabPane>  
                );  
              })}  
            </Tabs>  
          </div>  
        )}  
      </Card>  
  
      {/* Display unscheduled applications if any */}  
      {Object.keys(unscheduledApplicationsPerVenue).length > 0 && (  
        <Card bordered={false} style={{ marginTop: "20px" }}>  
          <Title level={4}>Unscheduled Applications</Title>  
          <Tabs  
            activeKey={selectedVenueTab}  
            onChange={(key) => setSelectedVenueTab(key)}  
            type="card"  
          >  
            {Object.keys(unscheduledApplicationsPerVenue).map((venueId) => {  
              const applications =  
                unscheduledApplicationsPerVenue[venueId];  
  
              // Skip if there are no unscheduled applications for this venue  
              if (!applications || applications.length === 0) return null;  
  
              const venueName =  
                venueId === "noVenue"  
                  ? "No Venue"  
                  : venues.find(  
                      (venue) => venue._id === venueId  
                    )?.venue || "Unknown Venue";  
  
              // Prepare dataSource for the Table  
              const dataSource = applications.map((application) => ({  
                ...application,  
                key: application._id,  
              }));  
  
              return (  
                <Tabs.TabPane tab={venueName} key={venueId}>  
                  <Table  
                    dataSource={dataSource}  
                    columns={[  
                      {  
                        title: "Participants",  
                        dataIndex: "",  
                        key: "participants",  
                        render: (application) => {  
                          if (application.group && application.group.group_name) {  
                            const memberNames = (application.group.group || []).map(m => m.name).join(', ');  
                            return `${application.group.group_name}${memberNames ? ': ' + memberNames : ''}`;  
                          } else if (application.contestant) {  
                            return `${application.contestant.first_name} ${application.contestant.last_name}`;  
                          } else {  
                            return "No Contestants";  
                          }  
                        },  
                      },  
                      {  
                        title: "Group Type",  
                        dataIndex: "",  
                        key: "groupType",  
                        render: (application) => {  
                          if (  
                            application.group &&  
                            application.group.group_size  
                          ) {  
                            return mapSizeToType(  
                              application.group.group_size  
                            );  
                          } else {  
                            return "solo";  
                          }  
                        },  
                      },  
                      {  
                        title: "Event",  
                        dataIndex: "event",  
                        key: "event",  
                        render: (event) =>  
                          event  
                            ? `${event.name} - ${event.style}`  
                            : "No Event",  
                      },  
                    ]}  
                    rowKey="key"  
                    pagination={{ pageSize: 10 }}  
                    scroll={{ x: true }}  
                  />  
                </Tabs.TabPane>  
              );  
            })}  
          </Tabs>  
        </Card>  
      )}  
    </div>  
  );  
};  
  
export default SchedulingPage;  